// Bloque "Mercado Libre" dentro de Editar producto: sólo aparece si el producto está vinculado
// a una publicación (lo trajo de Mercado Libre o lo vinculó). Muestra calidad y visitas, avisa
// cuando Mercado Libre pide más fotos, y deja cambiar precio y descripción con el "¿seguro?"
// del backend (ADR-2026-09-28-mercadolibre-precio-y-descripcion-con-reglas, en atlas-backend).

import React from "react";
import { AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useUserGroup } from "../hooks/useUserGroup";
import { ErrorDeMercadoLibre, veMercadoLibre } from "../lib/mercadoLibreConexionLogica";
import {
  calidadDeMl,
  cambiarDatosEnMl,
  descripcionDeMl,
  esperarCambioEnMl,
  publicacionesDelProducto,
  visitasDeMl,
} from "../lib/mercadoLibreDatos";
import {
  estadoLegible,
  formatoPrecio,
  leerPrecioEscrito,
  PIDEN_CONFIRMAR,
  resumenDeCambio,
  type CalidadDeMl,
  type PublicacionDelProducto,
} from "../lib/mercadoLibreDatosLogica";
import { MercadoLibreIcon } from "./MercadoLibreIcon";

/** Cuántas publicaciones de una familia se muestran con su precio. */
const MAX_VISIBLES = 8;

export const MercadoLibreFicha: React.FC<{ varianteId: string; companyId: string | null }> = ({ varianteId, companyId }) => {
  const { isDevTester } = useUserGroup();
  const ve = veMercadoLibre(isDevTester);
  const [pubs, setPubs] = React.useState<PublicacionDelProducto[] | null>(null);
  const [calidad, setCalidad] = React.useState<CalidadDeMl | null>(null);
  const [visitas, setVisitas] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!ve || !companyId) return;
    let cancelado = false;
    (async () => {
      const { data } = await supabase.from("variantes").select("product_id").eq("id", varianteId).eq("id_company", companyId).maybeSingle();
      const productoId = (data as { product_id?: string } | null)?.product_id;
      if (!productoId) return;
      const lista = await publicacionesDelProducto(productoId).catch(() => []);
      if (cancelado) return;
      setPubs(lista);
      const principal = lista[0];
      if (!principal) return;
      calidadDeMl(principal.item_id)
        .then((c) => !cancelado && setCalidad(c))
        .catch(() => {});
      visitasDeMl(principal.item_id)
        .then((v) => !cancelado && setVisitas(v))
        .catch(() => {});
    })();
    return () => {
      cancelado = true;
    };
  }, [ve, companyId, varianteId]);

  // No vinculado a Mercado Libre (o no lo ve): no se dibuja nada.
  if (!ve || !pubs || pubs.length === 0) return null;
  const principal = pubs[0]!;

  return (
    <div className="rounded-xl border border-black/[0.07] bg-white p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-[14px] font-medium text-brand-black/80">
          <MercadoLibreIcon className="h-4 w-4" />
          Mercado Libre
        </span>
        {principal.permalink && (
          <a
            href={principal.permalink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[12.5px] text-brand-primary hover:opacity-70"
          >
            Ver publicación <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      {/* Calidad y visitas de la publicación principal */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-brand-black/60">
        {calidad && !calidad.sin_calcular && calidad.puntaje !== null && (
          <span>
            Calidad: <span className="font-medium text-brand-black/80">{calidad.nivel ?? "—"}</span> ({calidad.puntaje}/100)
          </span>
        )}
        {calidad?.sin_calcular && <span>Mercado Libre todavía no calculó la calidad.</span>}
        {visitas !== null && (
          <span>
            <span className="font-medium text-brand-black/80">{visitas.toLocaleString("es-AR")}</span> visitas en 30 días
          </span>
        )}
      </div>
      {calidad?.faltan_fotos && (
        <p className="text-[12.5px] leading-snug text-amber-700">
          Mercado Libre pide al menos 3 fotos en esta publicación. Generá más con este producto y subilas desde
          Creaciones → Más → Subir a Mercado Libre.
        </p>
      )}

      {/* Precio por publicación */}
      <div className="space-y-2">
        {pubs.slice(0, MAX_VISIBLES).map((p) => (
          <FilaDePrecio key={p.item_id} pub={p} mostrarTitulo={pubs.length > 1} />
        ))}
        {pubs.length > MAX_VISIBLES && (
          <p className="text-[12px] text-brand-black/45">Y {pubs.length - MAX_VISIBLES} publicaciones más de esta familia.</p>
        )}
      </div>

      <EditorDeDescripcion itemId={principal.item_id} />
    </div>
  );
};

/** Aplica un cambio y espera el resultado. Maneja el "¿seguro?" (409) del backend. */
function useCambio() {
  const [estado, setEstado] = React.useState<"quieto" | "guardando" | "confirmar">("quieto");
  const [mensaje, setMensaje] = React.useState<{ texto: string; ok: boolean } | null>(null);
  const pendiente = React.useRef<Parameters<typeof cambiarDatosEnMl>[0] | null>(null);

  const aplicar = async (p: Parameters<typeof cambiarDatosEnMl>[0]) => {
    setEstado("guardando");
    setMensaje(null);
    try {
      const id = await cambiarDatosEnMl(p);
      const r = await esperarCambioEnMl(id);
      setMensaje(resumenDeCambio(r));
      setEstado("quieto");
      return true;
    } catch (e) {
      if (e instanceof ErrorDeMercadoLibre && PIDEN_CONFIRMAR.has(e.code)) {
        pendiente.current = p;
        setMensaje({ texto: e.message, ok: false });
        setEstado("confirmar");
        return false;
      }
      setMensaje({ texto: e instanceof Error ? e.message : "No se pudo guardar.", ok: false });
      setEstado("quieto");
      return false;
    }
  };
  const confirmar = () => (pendiente.current ? aplicar({ ...pendiente.current, confirmado: true }) : Promise.resolve(false));
  const cancelar = () => {
    pendiente.current = null;
    setMensaje(null);
    setEstado("quieto");
  };
  return { estado, mensaje, aplicar, confirmar, cancelar };
}

const FilaDePrecio: React.FC<{ pub: PublicacionDelProducto; mostrarTitulo: boolean }> = ({ pub, mostrarTitulo }) => {
  const [valor, setValor] = React.useState(pub.precio ? String(Number(pub.precio)) : "");
  const [precio, setPrecio] = React.useState(pub.precio);
  const c = useCambio();
  const nuevo = leerPrecioEscrito(valor);
  const cambio = nuevo !== null && nuevo !== Number(precio);
  const bloqueada = pub.precio_automatico || pub.estado === "closed";

  const guardar = async () => {
    if (!cambio || nuevo === null) return;
    if (await c.aplicar({ itemId: pub.item_id, precio: nuevo })) setPrecio(String(nuevo));
  };
  const confirmar = async () => {
    if ((await c.confirmar()) && nuevo !== null) setPrecio(String(nuevo));
  };

  return (
    <div>
      {mostrarTitulo && <p className="text-[12px] text-brand-black/50 truncate">{pub.titulo ?? pub.item_id}</p>}
      <div className="flex items-center gap-2">
        <span className="text-[12.5px] text-brand-black/55 w-[54px] shrink-0">Precio</span>
        {bloqueada ? (
          <span className="text-[13px] text-brand-black/70">
            {formatoPrecio(precio, pub.moneda)}{" "}
            <span className="text-brand-black/40">
              · {pub.precio_automatico ? "precio automático de Mercado Libre" : estadoLegible(pub.estado)}
            </span>
          </span>
        ) : (
          <>
            <input
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              inputMode="decimal"
              disabled={c.estado !== "quieto"}
              className="w-[120px] h-8 rounded-md border border-neutral-200 px-2 text-[13px] focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none disabled:opacity-60"
            />
            {cambio && c.estado === "quieto" && (
              <button type="button" onClick={guardar} className="text-[12.5px] font-medium text-brand-primary hover:opacity-70">
                Guardar
              </button>
            )}
            {c.estado === "guardando" && <Loader2 className="w-4 h-4 animate-spin text-brand-black/40" />}
            <span className="ml-auto text-[11.5px] text-brand-black/40">{estadoLegible(pub.estado)}</span>
          </>
        )}
      </div>
      <Aviso c={c} onConfirmar={confirmar} />
    </div>
  );
};

const EditorDeDescripcion: React.FC<{ itemId: string }> = ({ itemId }) => {
  const [abierto, setAbierto] = React.useState(false);
  const [texto, setTexto] = React.useState<string | null>(null);
  const [cargando, setCargando] = React.useState(false);
  const c = useCambio();

  const abrir = async () => {
    setAbierto(true);
    if (texto !== null) return;
    setCargando(true);
    setTexto(await descripcionDeMl(itemId).catch(() => ""));
    setCargando(false);
  };

  if (!abierto) {
    return (
      <button type="button" onClick={abrir} className="text-[12.5px] font-medium text-brand-primary hover:opacity-70">
        Editar descripción en Mercado Libre
      </button>
    );
  }
  return (
    <div className="space-y-1.5">
      <p className="text-[12.5px] text-brand-black/55">Descripción (sólo texto: Mercado Libre no acepta emojis ni formato)</p>
      {cargando ? (
        <Loader2 className="w-4 h-4 animate-spin text-brand-black/40" />
      ) : (
        <textarea
          value={texto ?? ""}
          onChange={(e) => setTexto(e.target.value)}
          rows={6}
          maxLength={50000}
          disabled={c.estado !== "quieto"}
          className="w-full rounded-md border border-neutral-200 px-2.5 py-2 text-[13px] leading-snug focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none disabled:opacity-60"
        />
      )}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void c.aplicar({ itemId, descripcion: texto ?? "" })}
          disabled={cargando || c.estado !== "quieto" || !(texto ?? "").trim()}
          className="h-8 px-3 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[12.5px] font-medium disabled:opacity-60"
        >
          {c.estado === "guardando" ? "Guardando…" : "Guardar descripción"}
        </button>
        <button type="button" onClick={() => setAbierto(false)} className="text-[12.5px] text-brand-black/50 hover:text-brand-black/80">
          Cerrar
        </button>
      </div>
      <Aviso c={c} onConfirmar={() => void c.confirmar()} />
    </div>
  );
};

const Aviso: React.FC<{ c: ReturnType<typeof useCambio>; onConfirmar: () => void }> = ({ c, onConfirmar }) => {
  if (!c.mensaje) return null;
  if (c.estado === "confirmar") {
    return (
      <div className="mt-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2 text-[12.5px] text-amber-800 space-y-1.5">
        <p className="inline-flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {c.mensaje.texto}
        </p>
        <div className="flex gap-3">
          <button type="button" onClick={onConfirmar} className="font-medium text-amber-900 underline underline-offset-2">
            Sí, cambiarlo
          </button>
          <button type="button" onClick={c.cancelar} className="text-amber-800/80">
            Cancelar
          </button>
        </div>
      </div>
    );
  }
  return <p className={`mt-1 text-[12.5px] ${c.mensaje.ok ? "text-emerald-700" : "text-amber-700"}`}>{c.mensaje.texto}</p>;
};

export default MercadoLibreFicha;
